// src/screens/ProfilePublic.tsx
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { BadgeCheck, User2 } from "lucide-react";
import i18n from "../i18n";

type Socials = {
  instagram?: string;
  twitter?: string;
  youtube?: string;
  tiktok?: string;
  website?: string;
};

type FavTeam = {
  id: string;
  name: string;
  league?: string | null;
  logo_path: string; // ex: "team-logos/Golden State Warriors.png"
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const ProfilePublic = () => {
  const { t, lng } = useI18nSafe();
  const locale = lng === "fr" ? "fr-FR" : "en-US";
  const { id } = useParams();

  const [profile, setProfile] = useState({
    full_name: "",
    bio: "",
    photo_url: "",
    club: "",
    niveau: "",
    is_premium: false,
    achievements: "",
    socials: {} as Socials,
    pseudo: "",
    public_use_pseudo: false,
  });
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [plays, setPlays] = useState<any[]>([]);

  // --- Équipes préférées ---
  const [favoriteTeams, setFavoriteTeams] = useState<FavTeam[]>([]);
  const [teamLogos, setTeamLogos] = useState<Record<string, string>>({});

  const publicLogoUrl = (logoPath: string) => {
    if (logoPath.startsWith("/")) return logoPath;
    if (logoPath.includes("/")) {
      const [bucket, ...rest] = logoPath.split("/");
      const key = rest.join("/");
      return supabase.storage.from(bucket).getPublicUrl(key).data.publicUrl;
    }
    return supabase.storage.from("team-logos").getPublicUrl(logoPath).data.publicUrl;
  };

  const ensureProtocol = (url?: string) => {
    if (!url) return "";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!id) return;

      const { data } = await supabase
        .from("user_profiles")
        .select(`
          full_name, bio, photo_url, club, niveau, is_premium, achievements,
          socials, pseudo, public_use_pseudo
        `)
        .eq("id", id)
        .single();

      if (data) {
        setProfile((prev) => ({
          ...prev,
          ...data,
          socials: (data.socials || {}) as Socials,
          pseudo: data.pseudo || "",
          public_use_pseudo: !!data.public_use_pseudo,
        }));
        if (data.photo_url) {
          const { data: urlData } = await supabase.storage
            .from("avatars")
            .createSignedUrl(data.photo_url, 3600);
          setSignedUrl(urlData?.signedUrl || "");
        } else {
          setSignedUrl("");
        }
      }
    };

    const loadPlays = async () => {
      if (!id) return;

      const { data } = await supabase
        .from("plays")
        .select("id, name, created_at")
        .eq("user_id", id)
        .eq("is_public", true)
        .order("created_at", { ascending: false });

      setPlays(data || []);
    };

    const loadFavoriteTeams = async () => {
      if (!id) return;

      const { data: favIdsRows, error: favErr } = await supabase
        .from("user_favorite_teams")
        .select("team_id")
        .eq("user_id", id);

      if (favErr) {
        console.error(favErr);
        setFavoriteTeams([]);
        setTeamLogos({});
        return;
      }

      const ids = (favIdsRows || []).map((r: any) => r.team_id);
      if (!ids.length) {
        setFavoriteTeams([]);
        setTeamLogos({});
        return;
      }

      const { data: teams } = await supabase
        .from("teams_catalog")
        .select("id,name,league,logo_path")
        .in("id", ids);

      const list = (teams || []) as FavTeam[];
      setFavoriteTeams(list);

      const entries = list.map((t) => [t.id, publicLogoUrl(t.logo_path)] as const);
      setTeamLogos(Object.fromEntries(entries));
    };

    Promise.all([loadProfile(), loadPlays(), loadFavoriteTeams()]).finally(() =>
      setLoading(false)
    );
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950">
        <p className="text-gray-600 dark:text-gray-400">{t("common:loading")}</p>
      </div>
    );
  }

  const achievementsList =
    profile.achievements?.trim()
      ? profile.achievements.split("\n").map((l) => l.trim()).filter(Boolean)
      : [];

  const hasSocials =
    !!(profile.socials?.instagram ||
       profile.socials?.twitter ||
       profile.socials?.youtube ||
       profile.socials?.tiktok ||
       profile.socials?.website);

  // Nom public : pseudo si autorisé, sinon nom complet
  const displayName =
    profile.public_use_pseudo
      ? (profile.pseudo || t("profilepublic:labels.user_fallback"))
      : (profile.full_name || t("profilepublic:labels.user_fallback"));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <span className="text-orange-600 dark:text-orange-400">
            {t("profilepublic:title", { name: displayName })}
          </span>
          {profile.is_premium && (
            <span className="text-yellow-500" title={t("profilepublic:premium.badge_title")}>
              <BadgeCheck className="w-6 h-6" />
            </span>
          )}
        </h1>

        {/* Carte profil */}
        <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-6">
          {signedUrl ? (
            <img
              src={signedUrl}
              alt={t("profilepublic:labels.avatar_alt")}
              className="w-32 h-32 rounded-full object-cover ring-2 ring-orange-500/20"
            />
          ) : (
            <div className="w-32 h-32 rounded-full grid place-items-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <User2 className="w-10 h-10 text-gray-400" aria-hidden="true" />
            </div>
          )}

          <div className="space-y-2">
            {/* Affiche soit le pseudo seul, soit le nom, selon le choix utilisateur */}
            {profile.public_use_pseudo ? (
              <p>
                <strong className="text-gray-700 dark:text-gray-300">
                  {t("profilepublic:labels.public_name")} :
                </strong>{" "}
                {profile.pseudo || t("profilepublic:labels.user_fallback")}
              </p>
            ) : (
              <>
                <p>
                  <strong className="text-gray-700 dark:text-gray-300">
                    {t("profilepublic:labels.name")} :
                  </strong>{" "}
                  {profile.full_name || t("profilepublic:labels.not_set")}
                </p>
                {profile.pseudo && (
                  <p>
                    <strong className="text-gray-700 dark:text-gray-300">
                      {t("profilepublic:labels.nickname")} :
                    </strong>{" "}
                    {profile.pseudo}
                  </p>
                )}
              </>
            )}

            <p>
              <strong className="text-gray-700 dark:text-gray-300">
                {t("profilepublic:labels.club")} :
              </strong>{" "}
              {profile.club || t("profilepublic:labels.not_set")}
            </p>
            <p>
              <strong className="text-gray-700 dark:text-gray-300">
                {t("profilepublic:labels.level")} :
              </strong>{" "}
              {profile.niveau || t("profilepublic:labels.not_set")}
            </p>

            {achievementsList.length > 0 && (
              <div className="pt-2">
                <strong className="text-gray-700 dark:text-gray-300">
                  {t("profilepublic:labels.achievements")} :
                </strong>
                <ul className="mt-2 list-disc list-inside space-y-1 text-gray-800 dark:text-gray-200">
                  {achievementsList.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-2">
              <strong className="text-gray-700 dark:text-gray-300">
                {t("profilepublic:labels.bio")} :
              </strong>
              <p className="mt-1 text-gray-800 dark:text-gray-200">
                {profile.bio || t("profilepublic:labels.not_set")}
              </p>
            </div>

            {/* Réseaux & liens */}
            {hasSocials && (
              <div className="pt-2">
                <strong className="text-gray-700 dark:text-gray-300">
                  {t("profilepublic:labels.socials")} :
                </strong>
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {profile.socials.instagram && (
                    <a
                      href={ensureProtocol(profile.socials.instagram)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("profilepublic:social_names.instagram")}
                    </a>
                  )}
                  {profile.socials.twitter && (
                    <a
                      href={ensureProtocol(profile.socials.twitter)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("profilepublic:social_names.twitter")}
                    </a>
                  )}
                  {profile.socials.youtube && (
                    <a
                      href={ensureProtocol(profile.socials.youtube)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("profilepublic:social_names.youtube")}
                    </a>
                  )}
                  {profile.socials.tiktok && (
                    <a
                      href={ensureProtocol(profile.socials.tiktok)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("profilepublic:social_names.tiktok")}
                    </a>
                  )}
                  {profile.socials.website && (
                    <a
                      href={ensureProtocol(profile.socials.website)}
                      target="_blank" rel="noreferrer"
                      className="underline text-orange-600 dark:text-orange-400"
                    >
                      {t("profilepublic:social_names.website")}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Équipes préférées */}
          <div className="pt-4">
            <h2 className="text-lg font-semibold mb-2">
              {t("profilepublic:labels.teams_fav")}
            </h2>
            {favoriteTeams.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("profilepublic:labels.no_fav_teams")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {favoriteTeams.map((t_) => (
                  <div
                    key={t_.id}
                    className="flex items-center gap-2 border rounded-xl px-3 py-2 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm"
                    title={t_.name + (t_.league ? ` • ${t_.league}` : "")}
                  >
                    <img
                      src={teamLogos[t_.id] || ""}
                      alt={t_.name}
                      className="w-7 h-7 object-contain"
                    />
                    <div className="text-sm">
                      <div className="font-medium">{t_.name}</div>
                      {t_.league && <div className="text-xs text-gray-500">{t_.league}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Plays partagés */}
        <h2 className="text-xl font-semibold mt-8 mb-3">
          {t("profilepublic:labels.play_shared_by", { name: displayName })}
        </h2>

        {plays.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400">
            {t("profilepublic:labels.no_public_plays")}
          </p>
        ) : (
          <ul className="space-y-3">
            {plays.map((play) => (
              <li
                key={play.id}
                className="border border-gray-200 dark:border-gray-800 rounded-xl p-4 bg-white dark:bg-gray-900 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <p className="font-medium text-gray-900 dark:text-gray-100">{play.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("profilepublic:labels.created_at")} :{" "}
                  {new Date(play.created_at).toLocaleString(locale)}
                </p>
                <Link
                  to={`/play/${play.id}`}
                  className="inline-block mt-2 text-sm text-blue-600 dark:text-blue-400 underline hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                >
                  {t("profilepublic:labels.view_play")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
